import { io, Socket } from 'socket.io-client';
import { getApiBaseUrl } from '../config/api.config';
import { authService } from './auth.service';

/**
 * Learning Assistant V2 Service
 *
 * WebSocket-based streaming AI assistant service.
 * Provides real-time text and audio streaming capabilities.
 *
 * Features:
 * - Real-time text streaming (<200ms first chunk)
 * - Progress events during processing
 * - Session management with reconnection
 * - Context updates without reconnection
 * - Performance metrics tracking
 */

// Types
export interface TextChatStartRequest {
  session_id?: string;
  course_id: string;
  module_id?: string;
  scene_id?: string;
  scene_number?: number;
  message: string;
  language?: string;
  auto_detect_language?: boolean;
}

export interface TextChunk {
  session_id: string;
  chunk_id: string;
  text: string;
  chunk_index: number;
  timestamp: Date;
}

export interface TextChatComplete {
  session_id: string;
  total_chunks: number;
  suggestions: string[];
  references: any[];
  total_time_ms: number;
  average_chunk_time_ms: number;
  timestamp: Date;
}

export interface ChatMetrics {
  first_chunk_time_ms: number;
}

export interface ChatEvent {
  type: 'session_init' | 'context_loading' | 'context_loaded' | 'generating_response' | 'complete' | 'error';
  data?: any;
  error?: string;  // human-readable message on type:'error' events
  code?: string;   // error code on type:'error' events, e.g. 'out_of_credits'
  timestamp: Date;
}

export interface ChatError {
  error: string;
  code: string;
  error_description?: string; // human-readable message from the backend ErrorEntity
  error_code?: string;        // backend error code, e.g. 'out_of_credits' / 'plan_expired'
  timestamp: Date;
}

export interface ConnectionSuccess {
  client_id: string;
  message: string;
  version: string;
  features: {
    text_streaming: boolean;
    audio_streaming: boolean;
    context_updates: boolean;
    reconnection: boolean;
  };
  timestamp: Date;
}

export interface ContextUpdateRequest {
  session_id: string;
  course_id: string;
  module_id?: string;
  scene_id?: string;
  scene_number?: number;
}

export interface ContextUpdated {
  session_id: string;
  message: string;
  timestamp: Date;
}

// Audio types
  export interface AudioTranscriptionChunk {
    session_id: string;
    text: string;
    is_final: boolean;
    confidence?: number;
    timestamp: Date;
  }

  export interface AudioChunk {
    session_id: string;
    chunk_id: string;
    audio_data: string;
    format: string;
    sample_rate: number;
    is_complete: boolean;
    timestamp: Date;
  }

  export interface AudioChatComplete {
    session_id: string;
    total_time_ms: number;
    timestamp: Date;
  }

// OPTIMIZATION: New type for async suggestions
export interface SuggestionsReady {
  session_id: string;
  suggestions: string[];
  timestamp: Date;
}

// Event handlers
export interface StreamingEventHandlers {
  onConnect?: (data: ConnectionSuccess) => void;
  onChunk?: (chunk: TextChunk) => void;
  onComplete?: (data: TextChatComplete) => void;
  onMetrics?: (metrics: ChatMetrics) => void;
  onEvent?: (event: ChatEvent) => void;
  onError?: (error: ChatError) => void;
  onContextUpdated?: (data: ContextUpdated) => void;
  onDisconnect?: () => void;
   onAudioTranscription?: (chunk: AudioTranscriptionChunk) => void;
    onAudioChunk?: (chunk: AudioChunk) => void;
    onAudioComplete?: (data: AudioChatComplete) => void;
  // OPTIMIZATION: New handler for async suggestions (Phase 1)
  onSuggestionsReady?: (suggestions: string[]) => void;
}

/**
 * Get WebSocket URL from API URL
 */
function getWebSocketUrl(): string {
  const apiBaseUrl = getApiBaseUrl();

  // Convert HTTP/HTTPS to WS/WSS and remove any path base (like /skillzen-srv)
  let wsUrl = apiBaseUrl
    .replace('http://', 'ws://')
    .replace('https://', 'wss://');

  // Remove /skillzen-srv path if present (WebSocket connects directly to server)
  wsUrl = wsUrl.replace('/skillzen-srv', '');

  return `${wsUrl}/ws/ai-assistant-v2`;
}

/**
 * Learning Assistant V2 Service
 * Singleton service for WebSocket streaming communication
 *
 * Features:
 * - Persistent connection across component unmounts
 * - Multiple components can share the same connection
 * - Automatic context updates on navigation
 */
class LearningAssistantV2Service {
  public socket: Socket | null = null;  // Changed to public for audio streaming
  private currentSessionId: string | null = null;
  private eventHandlers: StreamingEventHandlers = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10; // BUG FIX: Increased from 5 to 10 for better reconnection coverage

  // Connection state management
  private connectionStateCallbacks: Set<(isConnected: boolean) => void> = new Set();
  private isConnectedState: boolean = false;

  // Message persistence for active sessions
  private activeStreamingMessageId: string | null = null;
  private persistedMessages: any[] = [];

  // BUG FIX: Response timeout tracking
  private responseTimeoutTimer: NodeJS.Timeout | null = null;
  private lastChunkTime: number | null = null;
  private readonly RESPONSE_TIMEOUT_MS = 45000; // 45 seconds without any chunks = timeout

  // Guard against re-entrant auth:expired handling. Multiple in-flight
  // messages can each trigger a separate auth:expired event before the
  // server's disconnect actually closes the transport — without this
  // flag we'd fire parallel refresh+reconnect loops.
  private isHandlingAuthExpired = false;

  /**
   * TOKEN REFRESH: Check if token is expired or about to expire
   * Returns refreshed token or current token if still valid
   */
  /**
   * Check if user has a valid auth session.
   * With cookie-based auth, tokens are in HttpOnly cookies — we can't read them directly.
   * The browser sends them automatically via withCredentials.
   */
  private isSessionValid(): boolean {
    return authService.isAuthenticated();
  }

  /**
   * Connect to WebSocket server
   * If already connected, registers new handlers and resolves immediately
   * TOKEN REFRESH: Checks and refreshes token before connecting
   */
  connect(handlers: StreamingEventHandlers): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        // Check if already connected
        if (this.socket?.connected) {
          console.log('[AIAssistantV2] Already connected, reusing existing connection');

          // Merge new handlers with existing ones
          this.eventHandlers = { ...this.eventHandlers, ...handlers };

          // Call onConnect handler immediately with cached connection data
          if (handlers.onConnect) {
            handlers.onConnect({
              client_id: this.socket.id || '',
              message: 'Reusing existing connection',
              version: '2.0',
              features: {
                text_streaming: true,
                audio_streaming: true,
                context_updates: true,
                reconnection: true,
              },
              timestamp: new Date(),
            });
          }

          resolve();
          return;
        }

        // Check if user has a valid session
        if (!this.isSessionValid()) {
          console.error('[AIAssistantV2] No valid auth session available');
          reject(new Error('No authentication session available'));
          return;
        }

        console.log('[AIAssistantV2] ✅ Session valid, proceeding with connection');

        // Store handlers (merge with existing if any)
        this.eventHandlers = { ...this.eventHandlers, ...handlers };

        // Get WebSocket URL
        const wsUrl = getWebSocketUrl();

        console.log('[AIAssistantV2] Connecting to:', wsUrl);

        // Create socket connection (cookies sent automatically via withCredentials)
        this.socket = io(wsUrl, {
          withCredentials: true,
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
        });

        // Connection success
        this.socket.on('connect', () => {
          console.log('[AIAssistantV2] Connected:', this.socket?.id);
          this.reconnectAttempts = 0;
          this.notifyConnectionStateChange(true);
        });

        // Connection confirmation from server
        this.socket.on('connection:success', (data: ConnectionSuccess) => {
          console.log('[AIAssistantV2] Connection confirmed:', data);
          this.eventHandlers.onConnect?.(data);
          resolve();
        });

        // Text chunk streaming
        this.socket.on('chat:text:chunk', (chunk: TextChunk) => {
          // BUG FIX: Reset timeout timer on each chunk
          this.resetResponseTimeout();
          this.eventHandlers.onChunk?.(chunk);
        });

        // Streaming complete
        this.socket.on('chat:text:complete', (data: TextChatComplete) => {
          // BUG FIX: Clear timeout timer on completion
          this.clearResponseTimeout();
          this.eventHandlers.onComplete?.(data);
        });

        // OPTIMIZATION: Listen for async suggestions (Phase 1)
        // Suggestions arrive separately after complete event for better performance
        this.socket.on('chat:suggestions:ready', (data: SuggestionsReady) => {
          console.log('✨ [SERVICE] Async suggestions received:', data.suggestions);
          this.eventHandlers.onSuggestionsReady?.(data.suggestions);
        });

        // Performance metrics
        this.socket.on('chat:metrics', (metrics: ChatMetrics) => {
          this.eventHandlers.onMetrics?.(metrics);
        });

        // Progress events
        this.socket.on('chat:event', (event: ChatEvent) => {
          this.eventHandlers.onEvent?.(event);
        });

        // Errors
        this.socket.on('chat:error', (error: ChatError) => {
          console.error('[AIAssistantV2] Error:', error);
          // BUG FIX: Clear timeout timer on error
          this.clearResponseTimeout();
          this.eventHandlers.onError?.(error);
        });

        // Context updated
        this.socket.on('context:updated', (data: ContextUpdated) => {
          this.eventHandlers.onContextUpdated?.(data);
        });

         // Audio event handlers
          this.socket.on('chat:audio:transcription', (chunk: AudioTranscriptionChunk) => {
            console.log('[AI VOICE CHECK LOG] [SERVICE] 🎙️ Transcription event received from server:', chunk);
            this.eventHandlers.onAudioTranscription?.(chunk);
          });

          this.socket.on('chat:audio:chunk', (chunk: AudioChunk) => {
            console.log('[AI VOICE CHECK LOG] [SERVICE] 🔊 Audio chunk received from server');
            this.eventHandlers.onAudioChunk?.(chunk);
          });

          this.socket.on('chat:audio:complete', (data: AudioChatComplete) => {
            console.log('[AI VOICE CHECK LOG] [SERVICE] ✅ Audio complete event received from server');
            this.eventHandlers.onAudioComplete?.(data);
          });

        // Auth expired: emitted by server when the per-message guard rejects
        // a mid-session token (the socket is disconnected immediately after).
        // Without this listener the disconnect reason 'io server disconnect'
        // would leave the user stuck (Socket.IO does not auto-reconnect on
        // server-initiated disconnects).
        this.socket.on('auth:expired', async (data: { reason?: string; message?: string }) => {
          if (this.isHandlingAuthExpired) {
            console.log('[AIAssistantV2] 🔐 auth:expired already in flight — ignoring duplicate');
            return;
          }
          this.isHandlingAuthExpired = true;

          console.warn('[AIAssistantV2] 🔐 Server signaled auth:expired:', data);

          this.clearResponseTimeout();

          try {
            const refreshed = await authService.refreshAccessToken();
            if (refreshed) {
              console.log('[AIAssistantV2] ✅ Token refreshed after auth:expired, reconnecting...');
              // The server has already disconnected us; tear down the old socket
              // and let connect() create a fresh one with the refreshed cookies.
              if (this.socket) {
                this.socket.disconnect();
                this.socket = null;
              }
              try {
                await this.connect(this.eventHandlers);
              } catch (e) {
                console.error('[AIAssistantV2] ❌ Reconnect after auth:expired failed', e);
                this.eventHandlers.onError?.({
                  error: 'Your session expired and reconnect failed. Please log in again.',
                  code: 'AUTH_EXPIRED',
                  timestamp: new Date(),
                });
              }
            } else {
              console.error('[AIAssistantV2] ❌ Token refresh failed after auth:expired, redirecting to login');
              this.eventHandlers.onError?.({
                error: 'Your session has expired. Please log in again.',
                code: 'AUTH_EXPIRED',
                timestamp: new Date(),
              });
              authService.clearSession();
              window.location.replace('/login');
            }
          } finally {
            this.isHandlingAuthExpired = false;
          }
        });

        // Disconnection
        this.socket.on('disconnect', (reason) => {
          console.warn('[AIAssistantV2] ⚠️ Disconnected - Raw reason:', reason);

          // BUG FIX: Clear any active timeout timers on disconnect
          this.clearResponseTimeout();

          this.notifyConnectionStateChange(false);

          // BUG FIX: Enhanced disconnect handling with better context
          // Provide disconnection reason to UI
          const disconnectReasons: Record<string, string> = {
            'io server disconnect': 'Server closed the connection',
            'io client disconnect': 'Client disconnected manually',
            'ping timeout': 'Connection timeout - no response from server',
            'transport close': 'Network connection lost',
            'transport error': 'Network error occurred'
          };

          const readableReason = disconnectReasons[reason] || `Disconnected: ${reason}`;
          console.log('[AIAssistantV2] Disconnect reason (readable):', readableReason);

          // Call disconnect handler to show UI notification
          this.eventHandlers.onDisconnect?.();

          // BUG FIX: Socket.IO handles automatic reconnection
          // We just need to inform the user that reconnection will be attempted
          const isManualDisconnect = reason === 'io client disconnect';

          if (!isManualDisconnect) {
            console.log('[AIAssistantV2] 🔄 Socket.IO will automatically attempt reconnection...');
            console.log('[AIAssistantV2] Reconnection config:', {
              enabled: true,
              maxAttempts: this.maxReconnectAttempts,
              delay: '1000-5000ms'
            });
          } else {
            console.log('[AIAssistantV2] ℹ️ Manual disconnect - no auto-reconnect');
          }
        });

        // BUG FIX: Handle reconnection success
        this.socket.on('reconnect', async (attemptNumber) => {
          console.log('[AIAssistantV2] ✅ Reconnected successfully after', attemptNumber, 'attempts');
          this.reconnectAttempts = 0; // Reset counter
          this.notifyConnectionStateChange(true);

          // Cookies are sent automatically on reconnection via withCredentials
          console.log('[AIAssistantV2] ✅ Reconnected with fresh cookies');

          // Emit a synthetic onConnect event to update UI
          if (this.eventHandlers.onConnect) {
            this.eventHandlers.onConnect({
              client_id: this.socket.id || '',
              message: 'Reusing existing connection', // Treat reconnect as reusing connection
              version: 'v2.0.0',
              features: {
                text_streaming: true,
                audio_streaming: true,
                context_updates: true,
                reconnection: true
              },
              timestamp: new Date()
            });
          }
        });

        // BUG FIX: Track reconnection attempts
        this.socket.on('reconnect_attempt', (attemptNumber) => {
          console.log('[AIAssistantV2] 🔄 Reconnection attempt #', attemptNumber);
        });

        this.socket.on('reconnect_failed', () => {
          console.error('[AIAssistantV2] ❌ Reconnection failed after all attempts');

          // BUG FIX: Notify UI that reconnection has completely failed
          this.eventHandlers.onError?.({
            error: 'Failed to reconnect after multiple attempts. Please check your connection and try again.',
            code: 'RECONNECT_FAILED',
            timestamp: new Date()
          });

          // Update connection state
          this.notifyConnectionStateChange(false);
        });

        // Connection errors
        this.socket.on('connect_error', async (error) => {
          this.reconnectAttempts++;

          // BUG FIX: Reduce console spam - only log every 3rd attempt
          if (this.reconnectAttempts === 1 || this.reconnectAttempts % 3 === 0) {
            console.warn('[AIAssistantV2] ⚠️ Connection error (attempt', this.reconnectAttempts + '):', error.message || 'websocket error');
          }

          // TOKEN REFRESH: Check if it's an authentication error.
          // Server sends 'Unauthorized' (capital U) from the WS handshake
          // middleware, so the match must be case-insensitive.
          const errMsg = (error.message || '').toLowerCase();
          const isAuthError = errMsg.includes('unauthorized') ||
                            errMsg.includes('authentication') ||
                            errMsg.includes('401') ||
                            errMsg.includes('403');

          if (isAuthError) {
            console.log('[AIAssistantV2] 🔐 Authentication error detected, attempting token refresh...');

            // Try to refresh via the 401 interceptor (cookies are managed by server)
            const refreshed = await authService.refreshAccessToken();
            if (refreshed) {
              console.log('[AIAssistantV2] ✅ Token refreshed, Socket.IO will retry with new cookies');
            } else {
              console.error('[AIAssistantV2] ❌ Token refresh failed, user needs to re-login');
              // Emit error to show login required
              this.eventHandlers.onError?.({
                error: 'Your session has expired. Please log in again.',
                code: 'AUTH_EXPIRED',
                timestamp: new Date()
              });

              // Clear session and redirect to login
              authService.clearSession();
              window.location.replace('/login');
            }
          }

          // BUG FIX: Notify user if connection keeps failing
          if (this.reconnectAttempts === this.maxReconnectAttempts) {
            console.error('[AIAssistantV2] ❌ Max reconnection attempts reached:', this.maxReconnectAttempts);

            // Emit error to show user-friendly message
            this.eventHandlers.onError?.({
              error: 'Unable to reconnect to AI Assistant. Please check if the server is running and try again.',
              code: 'MAX_RECONNECT_ATTEMPTS',
              timestamp: new Date()
            });
          }
        });

      } catch (error) {
        console.error('[AIAssistantV2] Connection failed:', error);
        reject(error);
      }
    });
  }

  /**
   * BUG FIX: Start response timeout timer
   */
  private startResponseTimeout(): void {
    this.clearResponseTimeout(); // Clear any existing timer
    this.lastChunkTime = Date.now();

    this.responseTimeoutTimer = setTimeout(() => {
      const timeSinceLastChunk = Date.now() - (this.lastChunkTime || Date.now());
      console.error('[AIAssistantV2] ❌ Response timeout - no chunks received for', timeSinceLastChunk / 1000, 'seconds');

      // Emit error to handlers
      this.eventHandlers.onError?.({
        error: `Response timeout - no response received for ${this.RESPONSE_TIMEOUT_MS / 1000} seconds. Please try again or reconnect.`,
        code: 'RESPONSE_TIMEOUT',
        timestamp: new Date()
      });

      // Clear the timer
      this.clearResponseTimeout();
    }, this.RESPONSE_TIMEOUT_MS);
  }

  /**
   * BUG FIX: Clear response timeout timer
   */
  private clearResponseTimeout(): void {
    if (this.responseTimeoutTimer) {
      clearTimeout(this.responseTimeoutTimer);
      this.responseTimeoutTimer = null;
    }
  }

  /**
   * BUG FIX: Reset response timeout timer (called on each chunk received)
   */
  private resetResponseTimeout(): void {
    this.lastChunkTime = Date.now();
    // Restart the timer
    if (this.responseTimeoutTimer) {
      this.startResponseTimeout();
    }
  }

  /**
   * Start text chat streaming
   */
  startTextChat(request: TextChatStartRequest): void {
    if (!this.socket || !this.socket.connected) {
      throw new Error('WebSocket not connected');
    }

    console.log('🚀 [SERVICE] Starting text chat with language:', {
      language: request.language,
      message: request.message,
      session_id: request.session_id,
      timestamp: new Date().toISOString()
    });

    this.currentSessionId = request.session_id || null;

    // BUG FIX: Start timeout timer for this request
    this.startResponseTimeout();

    this.socket.emit('chat:text:start', request);

    console.log('✅ [SERVICE] Emitted chat:text:start to WebSocket');
  }

  /**
   * Update learning context
   */
  updateContext(request: ContextUpdateRequest): void {
    if (!this.socket || !this.socket.connected) {
      throw new Error('WebSocket not connected');
    }

    console.log('[AIAssistantV2] Updating context:', request);
    this.socket.emit('context:update', request);
  }

  /**
   * End current session
   */
  endSession(sessionId: string): void {
    if (!this.socket || !this.socket.connected) {
      throw new Error('WebSocket not connected');
    }

    console.log('[AIAssistantV2] Ending session:', sessionId);
    this.socket.emit('session:end', { session_id: sessionId });
    this.currentSessionId = null;
  }

  /**
   * Disconnect from WebSocket server
   * NOTE: This is a manual disconnect. Connection won't persist after this.
   * Clears all persisted messages and session data.
   */
  disconnect(): void {
    if (this.socket) {
      console.log('[AIAssistantV2] Manual disconnect requested');
      this.socket.disconnect();
      this.socket = null;
      this.currentSessionId = null;
      this.eventHandlers = {};
      this.persistedMessages = [];  // Clear messages on disconnect
      this.notifyConnectionStateChange(false);
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket !== null && this.socket.connected;
  }

  /**
   * Get current session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Start audio chat streaming
   */
  startAudioChat(request: {
    session_id?: string;
    course_id: string;
    module_id?: string;
    scene_id?: string;
    scene_number?: number;
    language?: string;
    return_audio?: boolean;
  }): void {
    if (!this.socket || !this.socket.connected) {
      throw new Error('WebSocket not connected');
    }

    console.log('[AI VOICE CHECK LOG] [SERVICE] 🎤 Starting audio chat - Emitting chat:audio:start event:', request);
    this.currentSessionId = request.session_id || null;
    this.socket.emit('chat:audio:start', request);
    console.log('[AI VOICE CHECK LOG] [SERVICE] ✅ Audio chat start event emitted successfully');
  }

  /**
   * Send audio chunk
   */
  sendAudioChunk(audioData: string): void {
    if (!this.socket || !this.socket.connected) {
      throw new Error('WebSocket not connected');
    }

    this.socket.emit('chat:audio:chunk', {
      audio_data: audioData
    });
  }

  /**
   * End audio streaming
   */
  endAudioStream(): void {
    if (!this.socket || !this.socket.connected) {
      throw new Error('WebSocket not connected');
    }

    console.log('[AIAssistantV2] Ending audio stream');
    this.socket.emit('chat:audio:end');
  }

  /**
   * Subscribe to connection state changes
   * @param callback Function to call when connection state changes
   * @returns Unsubscribe function
   */
  subscribeToConnectionState(callback: (isConnected: boolean) => void): () => void {
    this.connectionStateCallbacks.add(callback);

    // Immediately call with current state
    callback(this.isConnectedState);

    // Return unsubscribe function
    return () => {
      this.connectionStateCallbacks.delete(callback);
    };
  }

  /**
   * Notify all subscribers of connection state change
   */
  private notifyConnectionStateChange(isConnected: boolean): void {
    this.isConnectedState = isConnected;
    this.connectionStateCallbacks.forEach(callback => {
      try {
        callback(isConnected);
      } catch (error) {
        console.error('[AIAssistantV2] Error in connection state callback:', error);
      }
    });
  }

  /**
   * Set session ID (for external updates)
   */
  setSessionId(sessionId: string | null): void {
    this.currentSessionId = sessionId;
  }

  /**
   * Get persisted messages
   */
  getPersistedMessages(): any[] {
    return this.persistedMessages;
  }

  /**
   * Set persisted messages
   */
  setPersistedMessages(messages: any[]): void {
    this.persistedMessages = messages;
  }

  /**
   * Clear persisted messages
   */
  clearPersistedMessages(): void {
    this.persistedMessages = [];
  }
}

// Export singleton instance
export const learningAssistantV2Service = new LearningAssistantV2Service();
